/**
 * HTML backend — paints a scene as a CSS-flexbox HTML string.
 *
 * The counterpart to `toSVG`. Both consume the same DOM-free scene model,
 * so the interactive board (HTML) and the portable export (SVG) cannot
 * drift. Where `toSVG` runs the JS flex solver and emits absolute
 * `<rect>`/`<text>`, this backend maps the scene's flex *intent*
 * (direction/gap/padding/grow/align/justify) straight onto CSS flexbox and
 * lets the browser lay it out — preserving fluid resize + continuous-LOD
 * animation on the live board.
 *
 * Pure string output: no `document`, no `customElements`. The migrated
 * `archui-*` primitives build a scene and hand it here for their shadow
 * innerHTML.
 */
import {
  type Align,
  type BoxNode,
  type GlyphNode,
  type Gradient,
  type Justify,
  type ImageNode,
  type Node,
  type Overlay,
  type RichTextNode,
  type Shadow,
  type SlotNode,
  type TextNode,
  padOf,
} from "./model.js";

function gradientCss(g: Gradient): string {
  return `linear-gradient(${g.angle}deg, ${g.from}, ${g.to})`;
}

function shadowCss(s: Shadow): string {
  return `${s.dx}px ${s.dy}px ${s.blur}px ${s.spread ?? 0}px ${s.color}`;
}

/** CSS edge declarations pinning an overlay to its anchor inside a relative box. */
function overlayPins(ov: Overlay): Array<[string, string | number | undefined]> {
  const i = ov.inset ?? 0;
  const v = `${i}px`;
  const at = ov.at;
  const pins: Array<[string, string | number | undefined]> = [["position", "absolute"]];
  // Stretched on BOTH axes: the wrapper is full-size (pins below), but its inner
  // node would otherwise size to content, starving any grow child (e.g. a figure
  // widget's subspace portal → 0 height). `display:grid` puts the single child in
  // one cell where grid's default `place-items:stretch` makes it fill the wrapper
  // — matching layout.ts, which sizes the overlay node to the frame for SVG.
  if (ov.stretchX && ov.stretchY) pins.push(["display", "grid"]);
  if (ov.stretchX) { pins.push(["left", v], ["right", v]); }
  else if (at.includes("left")) pins.push(["left", v]);
  else if (at.includes("right")) pins.push(["right", v]);
  else pins.push(["left", "50%"], ["transform", "translateX(-50%)"]);
  if (ov.stretchY) { pins.push(["top", v], ["bottom", v]); }
  else if (at.startsWith("top")) pins.push(["top", v]);
  else if (at.startsWith("bottom")) pins.push(["bottom", v]);
  else if (at === "left" || at === "right") pins.push(["top", "50%"], ["transform", "translateY(-50%)"]);
  else pins.push(["top", v]);
  return pins;
}

function esc(s: string): string {
  return s
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;");
}

/** CSS `align-items` for a scene cross-axis alignment. */
function alignItems(a: Align | undefined): string {
  switch (a) {
    case "start": return "flex-start";
    case "center": return "center";
    case "end": return "flex-end";
    default: return "stretch"; // scene default
  }
}

/** CSS `justify-content` for a scene main-axis distribution. */
function justifyContent(j: Justify | undefined): string {
  switch (j) {
    case "center": return "center";
    case "end": return "flex-end";
    case "between": return "space-between";
    default: return "flex-start"; // scene default
  }
}

/** CSS `text-align` for a text node's horizontal placement. */
function textAlign(a: TextNode["style"]["align"]): string {
  return a === "center" ? "center" : a === "end" ? "right" : "left";
}

function styleAttr(decls: Array<[string, string | number | undefined]>): string {
  const css = decls
    .filter(([, v]) => v != null && v !== "")
    .map(([k, v]) => `${k}:${v}`)
    .join(";");
  return css ? ` style="${esc(css)}"` : "";
}

function boxHtml(node: BoxNode, fill = false): string {
  const [pt, pr, pb, pl] = padOf(node.padding);
  const s = node.style;
  const decls: Array<[string, string | number | undefined]> = [
    ["display", "flex"],
    ["flex-direction", node.direction],
    ["box-sizing", "border-box"],
    // Root fill: match today's `.card { width/height: 100% }` so the
    // widget's box drives the node's allotted size on the board.
    ["width", fill ? "100%" : node.width != null ? `${node.width}px` : undefined],
    ["height", fill ? "100%" : node.height != null ? `${node.height}px` : undefined],
    ["gap", node.gap ? `${node.gap}px` : undefined],
    [
      "padding",
      pt || pr || pb || pl ? `${pt}px ${pr}px ${pb}px ${pl}px` : undefined,
    ],
    ["align-items", node.align ? alignItems(node.align) : undefined],
    ["justify-content", node.justify ? justifyContent(node.justify) : undefined],
    ["flex-grow", node.growBand ? undefined : node.grow || undefined],
    // Let grow boxes (e.g. equal table cells) shrink below content width.
    ["min-width", node.grow || node.growBand ? 0 : undefined],
    // box paint
    ["background", s?.gradient ? gradientCss(s.gradient) : s?.fill],
    [
      "border",
      s?.stroke
        ? `${s.strokeWidth ?? 1}px ${s.dashed ? "dashed" : "solid"} ${s.stroke}`
        : undefined,
    ],
    ["border-radius", s?.shape === "ellipse" ? "50%" : s?.radius != null ? (Array.isArray(s.radius) ? s.radius.map((r) => `${r}px`).join(" ") : `${s.radius}px`) : undefined],
    ["box-shadow", s?.shadow ? shadowCss(s.shadow) : undefined],
    // LOD: opacity gated on the reveal band (full detail when unset on the host).
    ["opacity", node.reveal ? `var(--arch-band-${node.reveal}-reveal, 1)` : (s?.opacity != null && s.opacity < 1 ? s.opacity : undefined)],
    // LOD: collapse + grow with the band (0 flex-basis so it truly collapses).
    ["flex", node.growBand ? `calc(${node.grow ?? 1} * var(--arch-band-${node.growBand}, 1)) 0 0` : undefined],
    // Overlays are absolutely positioned against this box; band-gated boxes
    // also clip so collapsing content doesn't overflow.
    ["position", node.overlays?.length ? "relative" : undefined],
    ["overflow", node.overlays?.length || node.growBand ? "hidden" : undefined],
  ];
  const kids = node.children.map(toHtmlNode).join("");
  const overlays = (node.overlays ?? [])
    .map((ov) => `<div${styleAttr(overlayPins(ov))}>${toHtmlNode(ov.node)}</div>`)
    .join("");
  return `<div${styleAttr(decls)}>${kids}${overlays}</div>`;
}

/** Font CSS decls — a `font` shorthand, or split parts with a band-shrunk
 *  size when `bandShrink` is set (bigger at far zoom, tapering as you zoom in). */
function fontDecls(style: TextNode["style"] | RichTextNode["style"]): Array<[string, string | number | undefined]> {
  const { font, bandShrink } = style;
  if (!bandShrink) return [["font", `${font.weight} ${font.size}px ${font.family}`]];
  return [
    ["font-family", font.family],
    ["font-weight", font.weight],
    ["font-size", `calc(${font.size}px + ${bandShrink}px * (1 - var(--arch-band-compact-reveal, 1)))`],
  ];
}

function textHtml(node: TextNode): string {
  const { color, align, ellipsis, wrap, maxLines } = node.style;
  const decls: Array<[string, string | number | undefined]> = [
    ...fontDecls(node.style),
    ["color", color],
    ["text-align", align ? textAlign(align) : undefined],
    ["flex-grow", node.grow || undefined],
    // min-width:0 lets an ellipsized text flex-item actually shrink below
    // its content width inside a row (the classic flexbox truncation fix).
    ["min-width", ellipsis || node.grow ? 0 : undefined],
    ["text-decoration", node.style.strike ? "line-through" : undefined],
  ];
  if (ellipsis) {
    decls.push(
      ["white-space", "nowrap"],
      ["overflow", "hidden"],
      ["text-overflow", "ellipsis"],
    );
  } else if (wrap) {
    decls.push(["overflow", "hidden"]);
    if (maxLines != null) {
      // Multi-line clamp; -webkit-* is the broadly-supported form.
      decls.push(
        ["display", "-webkit-box"],
        ["-webkit-line-clamp", maxLines],
        ["-webkit-box-orient", "vertical"],
      );
    }
  }
  return `<span${styleAttr(decls)}>${esc(node.text)}</span>`;
}

function richTextHtml(node: RichTextNode): string {
  const { color, align, wrap, maxLines } = node.style;
  const decls: Array<[string, string | number | undefined]> = [
    ...fontDecls(node.style),
    ["color", color],
    ["text-align", align ? textAlign(align) : undefined],
    ["flex-grow", node.grow || undefined],
    ["min-width", node.grow ? 0 : undefined],
    ["overflow", "hidden"],
  ];
  // Browser handles wrapping; clamp to maxLines when set.
  if (wrap !== false && maxLines != null) {
    decls.push(
      ["display", "-webkit-box"],
      ["-webkit-line-clamp", maxLines],
      ["-webkit-box-orient", "vertical"],
    );
  }
  const linkColor = node.style.linkColor ?? "#60a5fa";
  const inner = node.runs
    .map((r) => {
      if (r.glyph) {
        const g = r.glyph;
        const col = g.color ? `color:${g.color};` : "";
        return (
          `<svg viewBox="0 0 ${g.viewW} ${g.viewH}" preserveAspectRatio="xMidYMid meet" ` +
          `style="display:inline-block;width:${g.width}px;height:${g.height}px;vertical-align:-0.15em;${col}">${g.body}</svg> `
        );
      }
      let t = esc(r.text);
      if (r.code) {
        const mono = node.style.monoFamily ?? "ui-monospace, monospace";
        t = `<code style="font-family:${esc(mono)};background:rgba(148,163,184,0.16);border-radius:3px;padding:0 3px">${t}</code>`;
      }
      if (r.strike) t = `<s>${t}</s>`;
      if (r.italic) t = `<em>${t}</em>`;
      if (r.bold) t = `<strong>${t}</strong>`;
      if (r.href) t = `<a href="${esc(r.href)}" style="color:${esc(linkColor)}">${t}</a>`;
      if (r.dim) t = `<span style="color:#94a3b8">${t}</span>`;
      return t;
    })
    .join("");
  return `<div${styleAttr(decls)}>${inner}</div>`;
}

function glyphHtml(node: GlyphNode): string {
  const decls: Array<[string, string | number | undefined]> = [
    ["width", node.width != null ? `${node.width}px` : undefined],
    ["height", node.height != null ? `${node.height}px` : undefined],
    // A growing glyph must also SHRINK (the geometry backend's growers absorb
    // overflow): a figure glyph composed at the node's full dims gives back
    // the caption's height, or the name paints below the card and is clipped.
    ["flex", node.grow != null ? `${node.grow} 1 auto` : node.width != null ? "0 0 auto" : undefined],
    ["color", node.color],
    ["opacity", node.opacity != null && node.opacity < 1 ? node.opacity : undefined],
    ["overflow", "visible"],
  ];
  // Inherited by every stroke in `body` — the only way to dash a shape the
  // glyph draws itself (cylinder, device figures).
  const dash = node.dashed ? ` stroke-dasharray="4 3"` : "";
  return (
    `<svg${styleAttr(decls)} viewBox="0 0 ${node.viewW} ${node.viewH}" ` +
    `preserveAspectRatio="xMidYMid meet"${dash}>${node.body}</svg>`
  );
}

function imageHtml(node: ImageNode): string {
  // Fit into the node's box without distortion (mirrors the SVG `meet`).
  const decls: Array<[string, string | number | undefined]> = [
    ["width", `${node.width}px`],
    ["height", `${node.height}px`],
    ["object-fit", "contain"],
    ["flex", "0 0 auto"],
  ];
  return `<img src="${esc(node.src)}" alt="${esc(node.alt)}"${styleAttr(decls)}>`;
}

function slotHtml(node: SlotNode): string {
  // The board measures this element's rect ([part="subspace"]) and
  // projects the nested space over it; the inner <slot> also receives any
  // light-DOM children the host assigns. Fills its flex cell.
  const part = node.part ? ` part="${esc(node.part)}"` : "";
  const slot = `<slot${node.name ? ` name="${esc(node.name)}"` : ""}></slot>`;
  const decls: Array<[string, string | number | undefined]> = [
    ["display", "block"],
    // No `width:100%`: in a ROW parent (the described-container column split)
    // a percentage width becomes the flex BASIS and starts a shrink war the
    // layout solver doesn't model — the two backends then disagree on the
    // column split. Cross-axis fill in a column comes from align stretch
    // (the CSS default, and the solver's default too).
    // Containing block for the projected nested space. The board slots an
    // `absolute inset-0` wrapper (SpaceRenderer's NestedSpaceFit) into this
    // div; `<slot>` is `display:contents` and can't be a containing block, so
    // without `position:relative` HERE the wrapper resolves to the card root
    // and paints over the header (the R3 subspace-overlap bug). No `height`:
    // the slotted wrapper is out-of-flow (0 content height), so `flex-grow`
    // alone must size the slot — a `height:100%` would double-count against
    // this div's flex siblings and shove the body off the card.
    ["position", "relative"],
    ["flex-grow", node.grow || undefined],
  ];
  return `<div${part}${styleAttr(decls)}>${slot}</div>`;
}

function toHtmlNode(node: Node): string {
  switch (node.type) {
    case "box": return boxHtml(node);
    case "text": return textHtml(node);
    case "richtext": return richTextHtml(node);
    case "glyph": return glyphHtml(node);
    case "image": return imageHtml(node);
    case "slot": return slotHtml(node);
    case "spacer":
      return `<div style="flex:${node.grow ?? 1} 1 auto"></div>`;
  }
}

export interface ToHtmlOptions {
  /** Background fill behind the whole scene (omit for transparent). */
  readonly background?: string;
}

/**
 * Render a scene to an HTML string laid out by CSS flexbox.
 *
 * Wraps the root in a full-size host so a widget can drop the output
 * straight into its shadow root and have it fill the node's box. The root
 * node is forced to fill the host (matching today's `.card{width/height:
 * 100%}`) unless it carries its own fixed size.
 */
export function toHTML(root: Node, opts: ToHtmlOptions = {}): string {
  const inner =
    root.type === "box" && root.width == null && root.height == null
      ? boxHtml(root, true)
      : toHtmlNode(root);
  const host: Array<[string, string | number | undefined]> = [
    ["display", "flex"],
    ["width", "100%"],
    ["height", "100%"],
    ["box-sizing", "border-box"],
    ["background", opts.background],
  ];
  return `<div${styleAttr(host)}>${inner}</div>`;
}
